import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ActiveAlertsResponse,
  AlertConfig,
  AlertsListResponse,
  CreateAlertInput,
  UpdateAlertInput,
} from "@money/shared";
import { apiFetch } from "./client";

export const alertsKey = ["alerts"] as const;

export function useAlerts() {
  return useQuery({
    queryKey: alertsKey,
    queryFn: () => apiFetch<AlertsListResponse>("/alerts"),
    staleTime: 60_000,
  });
}

export function useActiveAlerts() {
  return useQuery({
    queryKey: ["alerts", "active"] as const,
    queryFn: () => apiFetch<ActiveAlertsResponse>("/alerts/active"),
    staleTime: 60_000,
  });
}

export function useCreateAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAlertInput) =>
      apiFetch<AlertConfig>("/alerts", { method: "POST", body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
}

export function useUpdateAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; patch: UpdateAlertInput }) =>
      apiFetch<AlertConfig>(`/alerts/${input.id}`, { method: "PATCH", body: input.patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
}

export function useDeleteAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/alerts/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
}
